import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { API, api, getJwt, fileSize } from '../lib/api'
import { useUploads } from '../lib/uploads'
import { slugify } from '../lib/slug'
import Layout, { StatusBadge } from '../components/Layout'
import { ClientForm, MeetingForm, ProjectForm } from '../components/Forms'
import { InvoiceEditor } from '../components/InvoiceEditor'
import { Btn, Card, Empty, Msg, money, fmtDate, fmtDateTime } from '../components/ui'
import ReviewLink from '../components/ReviewLink'
import { clientLabel, inviteClient, type Client, type Contract, type Invoice, type Meeting, type Project, type Video } from '../lib/db'

interface F { name: string; size: number; modified: string }

export default function ClientDetail() {
  const { id } = useParams(); const nav = useNavigate()
  const { start } = useUploads()
  const [c, setC] = useState<Client | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [videos, setVideos] = useState<Video[]>([])
  const [contracts, setContracts] = useState<Contract[]>([])
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [files, setFiles] = useState<F[]>([])
  const [jwt, setJwt] = useState('')
  const [msg, setMsg] = useState('')
  const [pct, setPct] = useState<number | null>(null)
  const [modal, setModal] = useState<null | { type: 'client' } | { type: 'project'; data?: Project } | { type: 'invoice'; data?: Invoice; projectId?: string } | { type: 'meeting'; data?: Meeting }>(null)
  const slug = c ? slugify(c.full_name || c.company) : ''

  async function load() {
    const { data } = await supabase.from('clients').select('*').eq('id', id).maybeSingle()
    setC(data as any); if (!data) return
    const [p, i, v, k, m] = await Promise.all([
      supabase.from('projects').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('invoices').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('videos').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('contracts').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('meetings').select('*').eq('client_id', id).order('meeting_date', { ascending: false }),
    ])
    setProjects((p.data as any) || []); setInvoices((i.data as any) || []); setVideos((v.data as any) || []); setContracts((k.data as any) || []); setMeetings((m.data as any) || [])
  }
  async function loadFiles(s: string) {
    if (!s) return
    try { const j = await api<{ files: F[] }>(`/deliverables?slug=${s}`); setFiles(j.files || []) } catch { setFiles([]) }
  }
  useEffect(() => { getJwt().then(setJwt); load() }, [id])
  useEffect(() => { if (slug) loadFiles(slug) }, [slug])

  async function invite() {
    if (!c) return
    try { await inviteClient(c.email, c.full_name); setMsg(`✅ Invitation envoyée à ${c.email}.`) } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }
  async function remove() {
    if (!c || !confirm(`Supprimer définitivement ${clientLabel(c)} et toutes ses données (projets, factures, vidéos) ?`)) return
    const { error } = await supabase.from('clients').delete().eq('id', c.id)
    if (error) { setMsg('Erreur : ' + error.message); return }
    nav('/admin/clients')
  }
  async function uploadFile(e: React.ChangeEvent<HTMLInputElement>, sub?: string) {
    const list = Array.from(e.target.files || []); if (!list.length || !slug) return
    setPct(0); setMsg('')
    try {
      const r = await start(`/upload-deliverable?slug=${slug}${sub ? '&sub=' + sub : ''}`, list)
      if (sub === 'contrats' && projects.length) {
        for (const name of r.files as string[]) {
          const f = list.find(x => name.startsWith(x.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 60))) || list[0]
          await supabase.from('contracts').insert({ client_id: c!.id, project_id: projects[0].id, title: f.name.replace(/\.[^.]+$/, ''), filename: name, file_path: `${slug}/contrats/${name}`, file_size: f.size })
        }
        await load()
      } else if (sub === 'contrats') setMsg('Erreur : créez d\'abord un projet pour rattacher le contrat.')
      await loadFiles(slug); if (!msg) setMsg('✅ Fichier(s) téléversé(s).')
    } catch (err: any) { setMsg('Erreur : ' + err.message) } finally { setPct(null); e.target.value = '' }
  }
  async function openReview(name: string) {
    const file_path = `${slug}/${name}`
    const { data: ex } = await supabase.from('videos').select('id').eq('file_path', file_path).maybeSingle()
    let vid = (ex as any)?.id
    if (!vid) { const { data } = await supabase.from('videos').insert({ title: name.replace(/\.[^.]+$/, ''), file_name: name, file_path, client_id: c!.id, project_id: projects[0]?.id || null }).select('id').single(); vid = (data as any)?.id }
    if (vid) nav(`/revue/${vid}`)
  }
  async function markSent(k: Contract) { await supabase.from('contracts').update({ sent_at: new Date().toISOString() }).eq('id', k.id); load() }
  async function deleteContract(k: Contract) {
    if (!confirm(`Supprimer le contrat « ${k.title} » ?`)) return
    await supabase.from('contracts').delete().eq('id', k.id); load()
  }
  const dl = (name: string) => `${API}/deliverable?slug=${slug}&name=${encodeURIComponent(name)}&access_token=${jwt}`

  if (!c) return <Layout title="Client"><p className="text-acier">Chargement…</p></Layout>
  const info = [c.email, c.phone, [c.address, c.city, c.province, c.postal_code].filter(Boolean).join(', '), c.website].filter(Boolean)

  return (
    <Layout title={clientLabel(c)} actions={<>
      <Btn variant="ghost" onClick={() => setModal({ type: 'client' })}>Modifier</Btn>
      {!c.profile_id && <Btn onClick={invite}>✉ Inviter au portail</Btn>}
      <Btn variant="danger" onClick={remove}>Supprimer</Btn>
    </>}>
      <a href="/admin/clients" className="text-xs uppercase tracking-widest text-acier hover:text-orange">← Clients</a>
      <Msg msg={msg} />
      {pct !== null && <p className="text-xs text-acier mt-2">Envoi en cours (voir le panneau en bas à droite). Vous pouvez continuer à naviguer.</p>}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Card title="Coordonnées">
          <div className="text-sm space-y-1">
            <div className="font-medium">{c.full_name}{c.company ? <span className="text-acier"> · {c.company}</span> : ''}</div>
            {info.map(x => <div key={x} className="text-acier">{x}</div>)}
            <div className="pt-2 text-xs text-acier">Type : {c.type} · Langue : {c.language} {c.source ? `· Source : ${c.source}` : ''}</div>
            <div className="pt-1">{c.profile_id ? <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-green-500/15 text-green-300">Compte portail actif</span> : <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-yellow-500/15 text-yellow-300">Pas encore de compte</span>}</div>
            {c.notes && <p className="pt-3 text-acier whitespace-pre-line border-t border-white/5 mt-3">{c.notes}</p>}
            <p className="text-xs text-acier pt-2">Dossier livrables : <code className="text-orange">{slug}</code></p>
          </div>
        </Card>

        <Card title="Projets" className="lg:col-span-2" actions={<Btn small onClick={() => setModal({ type: 'project' })}>+ Projet</Btn>}>
          {projects.length === 0 ? <Empty>Aucun projet.</Empty> : projects.map(p => (
            <div key={p.id} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3">
              <div className="min-w-0"><button onClick={() => setModal({ type: 'project', data: p })} className="hover:text-orange text-left truncate">{p.title}</button>
                <div className="text-xs text-acier">{p.shoot_date ? `tournage ${fmtDate(p.shoot_date)} · ` : ''}{p.delivery_date ? `livraison ${fmtDate(p.delivery_date)}` : ''}{p.budget ? ` · ${money(p.budget)}` : ''}</div></div>
              <div className="flex items-center gap-3"><Btn variant="link" small onClick={() => setModal({ type: 'invoice', projectId: p.id })}>Facturer</Btn><StatusBadge status={p.status} /></div>
            </div>
          ))}
        </Card>

        <Card title="Factures" actions={<Btn small onClick={() => setModal({ type: 'invoice' })}>+ Facture</Btn>}>
          {invoices.length === 0 ? <Empty>Aucune facture.</Empty> : invoices.map(i => (
            <div key={i.id} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3">
              <button onClick={() => setModal({ type: 'invoice', data: i })} className="hover:text-orange text-left">{i.invoice_number || 'Facture'}<div className="text-xs text-acier">échéance {fmtDate(i.due_date)}</div></button>
              <div className="flex items-center gap-2 whitespace-nowrap">{money(i.amount)}<StatusBadge status={i.status} /></div>
            </div>
          ))}
        </Card>

        <Card title="Rencontres" actions={<Btn small onClick={() => setModal({ type: 'meeting' })}>+ Rencontre</Btn>}>
          {meetings.length === 0 ? <Empty>Aucune rencontre.</Empty> : meetings.slice(0, 6).map(m => (
            <div key={m.id} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3">
              <button onClick={() => setModal({ type: 'meeting', data: m })} className="hover:text-orange text-left truncate">{m.title}<div className="text-xs text-acier">{fmtDateTime(m.meeting_date)} · {m.platform}</div></button>
              <StatusBadge status={m.status} />
            </div>
          ))}
        </Card>

        <Card title="Vidéos en revue">
          {videos.length === 0 ? <Empty>Aucune vidéo en revue. Cliquez « Revue » sur un livrable vidéo.</Empty> : videos.map(v => (
            <a key={v.id} href={`/revue/${v.id}`} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3 hover:text-orange">
              <span className="truncate">🎬 {v.title}</span><StatusBadge status={v.review_status} />
            </a>
          ))}
        </Card>

        <Card title="Livrables" className="lg:col-span-2" actions={<>
          <label className="px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-widest bg-orange hover:bg-orange-hover cursor-pointer">{pct !== null ? 'Envoi…' : '⬆ Téléverser'}<input type="file" multiple className="hidden" disabled={pct !== null} onChange={e => uploadFile(e)} /></label></>}>
          <div className="pb-3 mb-1 border-b border-white/10"><ReviewLink slug={slug} disabled={files.length === 0} onMsg={setMsg} /></div>
          {files.length === 0 ? <Empty>Aucun fichier livré.</Empty> : files.map(f => {
            const isVideo = /\.(mp4|mov|webm|m4v)$/i.test(f.name)
            return (
              <div key={f.name} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3">
                <span className="truncate">{isVideo ? '🎬' : /\.(jpe?g|png|gif|webp|heic|avif)$/i.test(f.name) ? '🖼️' : '📎'} {f.name} <span className="text-acier text-xs">· {fileSize(f.size)}</span></span>
                <span className="flex items-center gap-3 whitespace-nowrap text-xs uppercase tracking-widest">
                  {isVideo && <button onClick={() => openReview(f.name)} className="text-orange hover:underline">Revue</button>}
                  <a href={dl(f.name)} target="_blank" rel="noreferrer" className="text-acier hover:text-orange">Ouvrir</a>
                </span>
              </div>
            )
          })}
        </Card>

        <Card title="Contrats" actions={
          <label className="px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer">⬆ Ajouter (PDF)<input type="file" accept=".pdf,.doc,.docx" className="hidden" disabled={pct !== null} onChange={e => uploadFile(e, 'contrats')} /></label>}>
          {contracts.length === 0 ? <Empty>Aucun contrat.</Empty> : contracts.map(k => (
            <div key={k.id} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3">
              <a href={dl('contrats/' + k.filename)} target="_blank" rel="noreferrer" className="truncate hover:text-orange">📄 {k.title}</a>
              <span className="flex items-center gap-3 whitespace-nowrap text-xs uppercase tracking-widest">
                {!k.sent_at && <button onClick={() => markSent(k)} className="text-orange hover:underline">Marquer envoyé</button>}
                <StatusBadge status={k.viewed_at ? 'vu' : k.sent_at ? 'envoyé' : 'brouillon'} />
                <button onClick={() => deleteContract(k)} className="text-acier hover:text-red-300">×</button>
              </span>
            </div>
          ))}
        </Card>
      </div>

      {modal?.type === 'client' && <ClientForm initial={c} onClose={() => setModal(null)} onSaved={() => { setModal(null); load() }} />}
      {modal?.type === 'project' && <ProjectForm initial={modal.data || { client_id: c.id }} clients={[c]} onClose={() => setModal(null)} onSaved={() => { setModal(null); load() }} />}
      {modal?.type === 'invoice' && <InvoiceEditor initial={modal.data} clients={[c]} presetClientId={c.id} presetProjectId={modal.projectId} onClose={() => setModal(null)} onSaved={() => load()} />}
      {modal?.type === 'meeting' && <MeetingForm initial={modal.data || { client_id: c.id }} clients={[c]} onClose={() => setModal(null)} onSaved={() => { setModal(null); load() }} />}
    </Layout>
  )
}
